import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import './auth.css';
import { AuthShell, Field, PasswordField } from './AuthShell';
import { authService } from '../api/auth';
import { login } from '../store/authSlice';
import { AppDispatch } from '../../../store/store';

/**
 * Login — ported from docs/html-prototypes/CAR-IA/car-ia-login.html.
 * Copy is the prototype's Spanish wording. The prototype's Google/Apple
 * buttons are intentionally omitted: no OAuth provider is wired (and the
 * LinkedIn route was closed 2026-07-07), so rendering them would be a dead
 * control. Behaviour — credential login, unconfirmed-account redirect to
 * /verify-email, dispatch + navigate to /feed — is unchanged.
 */

interface FormErrors {
  email?: string;
  password?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!email.trim()) {
      next.email = 'El correo es obligatorio.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = 'Escribe un correo válido.';
    }
    if (!password) next.password = 'La contraseña es obligatoria.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError(null);
    try {
      const result = await authService.login({ email, password });
      dispatch(login({ user: result.user, accessToken: result.accessToken }));
      navigate('/feed');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'No pudimos iniciar sesión. Revisa tus datos e inténtalo de nuevo.';
      // Account exists but was never verified → send them to enter the code.
      if (/not confirmed|unconfirmed|usernotconfirmed/i.test(message)) {
        navigate('/verify-email', { state: { email } });
        return;
      }
      setApiError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      tab="login"
      title="Bienvenido de vuelta"
      subtitle="Ingresa para guardar vehículos, contactar vendedores y ver teléfonos."
    >
      {apiError && (
        <div className="cia-auth-alert cia-auth-alert--error" role="alert">
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <Field
          id="email"
          label="Correo"
          type="email"
          value={email}
          onChange={(v) => {
            setEmail(v);
            setErrors((p) => ({ ...p, email: undefined }));
            setApiError(null);
          }}
          error={errors.email}
          autoComplete="email"
        />
        <PasswordField
          id="password"
          label="Contraseña"
          value={password}
          onChange={(v) => {
            setPassword(v);
            setErrors((p) => ({ ...p, password: undefined }));
            setApiError(null);
          }}
          error={errors.password}
        />

        <div className="cia-auth-row2">
          <label className="cia-auth-remember">
            <input type="checkbox" defaultChecked />
            Recordarme
          </label>
          <button
            type="button"
            className="cia-auth-fpw"
            onClick={() => navigate('/forgot-password')}
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>

        <button type="submit" className="cia-auth-go" disabled={loading}>
          {loading ? <span className="cia-auth-spin" aria-label="Cargando" /> : 'Ingresar'}
        </button>

        <p className="cia-auth-alt">
          ¿No tienes cuenta?{' '}
          <button type="button" onClick={() => navigate('/register')}>
            Crear cuenta gratis
          </button>
        </p>
      </form>
    </AuthShell>
  );
};

export default Login;
